import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, CardHeader, ConfirmDialog, ErrorState, Icon, IconButton, MenuDropdown, PageHeader, PageLoader, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { DocPreview, siteText } from '../../../components/common/DocPreview/DocPreview';
import { PartyCard } from '../../../components/common/PartyCard/PartyCard';
import { ShareMenu } from '../../../components/common/ShareMenu/ShareMenu';
import { KeyValues, ReasonDialog, StatusBadge, TotalsBox, TwoColumn } from '../../../components/common/Parts/Parts';
import { useAction } from '../../../hooks/useAction';
import { useFetch } from '../../../hooks/useFetch';
import { INVOICE_STATUS, PAYMENT_STATUS } from '../../../constants/status';
import { labelOf } from '../../../utils/labels';
import { PAYMENT_MODES } from '../../../constants/payments';
import { formatDate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import { invoiceService } from '../services/invoiceService';

function PaymentsCard({ id, total }: { id: string; total: number }) {
  const { data } = useFetch(() => invoiceService.payments(id), [id, total]);
  if (!data?.length) return null;
  return (
    <Card>
      <CardHeader title="Payments" />
      <ul className="divide-y divide-line pt-2">
        {data.map((t) => (
          <li key={t._id}>
            <Link to={`/transactions/${t._id}`} className="flex items-center justify-between gap-3 px-5 py-2.5 hover:bg-navy-50/60">
              <span className="min-w-0">
                <span className="block text-sm font-medium text-navy-900">{formatDate(t.date)}</span>
                <span className="block text-xs text-ink-400">{labelOf(PAYMENT_MODES, t.mode)} · {t.transactionNo}{t.isReversed ? ' · reversed' : ''}</span>
              </span>
              <span className={`tabular text-sm font-semibold ${t.direction === 'credit' ? 'text-emerald-700' : 'text-red-600'} ${t.isReversed ? 'line-through opacity-60' : ''}`}>
                {money(t.amount)}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default function InvoiceDetails() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { busy, run } = useAction(toast);
  const [dialog, setDialog] = useState<'delete' | 'cancel' | 'finalize' | null>(null);
  const { data: inv, error, reload } = useFetch(() => invoiceService.get(id), [id]);
  const number = inv ? inv.invoiceNo ?? 'Draft invoice' : undefined;
  useCrumb(`/invoices/${id}`, number);

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!inv) return <PageLoader />;

  const isGst = inv.invoiceType === 'gst';
  const finalize = async () => {
    if (await run('finalize', () => invoiceService.finalize(id), 'Invoice finalized')) reload();
    setDialog(null);
  };
  const cancel = async (reason) => {
    if (await run('cancel', () => invoiceService.cancel(id, reason), 'Invoice cancelled')) reload();
    setDialog(null);
  };
  const remove = async () => {
    if (await run('delete', () => invoiceService.remove(id), 'Draft deleted')) navigate('/invoices', { replace: true });
    setDialog(null);
  };

  const menu = [
    inv.serviceId && { label: 'Open job', icon: 'wrench', onClick: () => navigate(`/jobs/${inv.serviceId}`) },
    inv.quotationId && { label: 'Open quotation', icon: 'quote', onClick: () => navigate(`/quotations/${inv.quotationId}`) },
    inv.status === 'final' && { label: 'Cancel invoice', icon: 'x', danger: true, onClick: () => setDialog('cancel') },
    inv.status === 'draft' && { label: 'Delete draft', icon: 'trash', danger: true, onClick: () => setDialog('delete') },
  ].filter(Boolean);

  return (
    <>
      <PageHeader
        title={number}
        badge={
          <span className="flex gap-2">
            <StatusBadge map={INVOICE_STATUS} value={inv.status} />
            {inv.status === 'final' && <StatusBadge map={PAYMENT_STATUS} value={inv.paymentStatus} />}
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <span className="inline-flex items-center gap-1.5"><Icon name="user" size={15} />{inv.customer?.name}</span>
            <span className="inline-flex items-center gap-1.5"><Icon name="receipt" size={15} />{isGst ? 'GST invoice' : 'Non-GST invoice'}</span>
            <span className="tabular inline-flex items-center gap-1.5"><Icon name="rupee" size={15} />{money(inv.grandTotal)}</span>
          </span>
        }
        actions={
          <>
            {inv.status === 'draft' && <Button variant="outline" leftIcon="edit" onClick={() => navigate(`/invoices/${id}/edit`)}>Edit</Button>}
            <Button variant="outline" leftIcon="download" loading={busy === 'pdf'} onClick={() => run('pdf', () => invoiceService.openPdf(id))}>Print / PDF</Button>
            {inv.status === 'final' && (
              <ShareMenu
                kind="invoices"
                id={id}
                docNo={inv.invoiceNo ?? 'invoice'}
                phone={inv.customer?.phone}
                shared={!!inv.shareToken}
                onChange={reload}
                message={`Hello ${inv.customer?.name}, please find invoice ${inv.invoiceNo} for ${money(inv.grandTotal)}.${inv.balanceAmount > 0 ? ` Balance due: ${money(inv.balanceAmount)}.` : ' Thank you for the payment.'}`}
              />
            )}
            {menu.length > 0 && <MenuDropdown trigger={<IconButton icon="more" label="More actions" variant="outline" />} items={menu} />}
            {inv.status === 'draft' && <Button leftIcon="checkCircle" onClick={() => setDialog('finalize')}>Issue invoice</Button>}
            {inv.status === 'final' && inv.balanceAmount > 0 && (
              <Button leftIcon="rupee" onClick={() => navigate(`/transactions/new?category=invoice_payment&invoiceId=${id}`)}>Record payment</Button>
            )}
          </>
        }
      />

      {inv.status === 'cancelled' && (
        <p className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">Cancelled on {formatDate(inv.cancelledAt)}: {inv.cancelReason}</p>
      )}

      <TwoColumn
        main={
          <DocPreview
            kind={isGst ? 'Tax invoice' : 'Invoice'}
            number={number}
            party={inv.customer}
            job={{ title: inv.serviceId ? 'Service job' : 'Work done', site: siteText(inv.siteAddress) }}
            dates={[
              ['Date', formatDate(inv.date)],
              ['Due date', inv.dueDate ? formatDate(inv.dueDate) : '-'],
              ['Place of supply', 'Maharashtra'],
            ]}
            isGst={isGst}
            sections={[
              {
                title: 'Items',
                lines: inv.items.map((l, i) => ({ key: l._id ?? String(i), text: l.description, sub: l.sacHsn ? `HSN/SAC ${l.sacHsn}` : '', qty: l.qty, unit: l.unit, rate: l.rate, gstRate: l.gstRate, amount: l.amount })),
              },
            ]}
            terms={inv.terms}
            footerNote={
              (inv.customerSuppliedMaterials?.length ?? 0) > 0 && (
                <div className="mt-4">
                  <p className="text-[11px] font-medium uppercase tracking-wider text-ink-400">Material supplied by customer (not charged)</p>
                  <ul className="mt-2 list-disc pl-5 text-[13px] text-ink-600">{inv.customerSuppliedMaterials?.map((m) => <li key={m}>{m}</li>)}</ul>
                </div>
              )
            }
            totals={<TotalsBox totals={inv} isGst={isGst} />}
          />
        }
        aside={
          <>
            <Card>
              <CardHeader title="Payment" />
              <div className="p-5 pt-4">
                <KeyValues
                  rows={[
                    ['Invoice total', money(inv.grandTotal), { strong: true }],
                    ['Received', money(inv.paidAmount), { tone: 'text-emerald-700' }],
                    ['Balance', money(inv.balanceAmount), { strong: true, tone: inv.balanceAmount > 0 ? 'text-red-600' : undefined }],
                  ]}
                />
                {inv.status === 'draft' && <p className="mt-4 rounded-lg bg-navy-50 p-3 text-[13px] text-ink-600">Issue the invoice to give it a number and record payments.</p>}
              </div>
            </Card>
            <PaymentsCard id={id} total={inv.paidAmount} />
            <PartyCard party={inv.customer} to={`/customers/${inv.customerId}`} whatsappText={inv.invoiceNo ? `Hello ${inv.customer?.name}, invoice ${inv.invoiceNo} for ${money(inv.grandTotal)}. Balance due ${money(inv.balanceAmount)}.` : undefined} />
          </>
        }
      />

      <ConfirmDialog
        open={dialog === 'finalize'}
        onClose={() => setDialog(null)}
        onConfirm={finalize}
        loading={busy === 'finalize'}
        title="Issue this invoice?"
        message="It gets its official number and can no longer be edited. If it came from a job, the job must be completed."
        confirmLabel="Issue invoice"
      />
      <ConfirmDialog open={dialog === 'delete'} onClose={() => setDialog(null)} onConfirm={remove} loading={busy === 'delete'} danger title="Delete this draft?" message="The draft will be removed for good." confirmLabel="Delete draft" />
      <ReasonDialog
        open={dialog === 'cancel'}
        onClose={() => setDialog(null)}
        onConfirm={cancel}
        loading={busy === 'cancel'}
        title="Cancel this invoice?"
        message="The number stays used and the invoice is kept as cancelled. Invoices with payments must have them reversed first."
        confirmLabel="Cancel invoice"
      />
    </>
  );
}
